import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import PageSEO from '../components/PageSEO';
import {
  BarChart3, Activity, Heart, Scale, Wind, Bone, Thermometer, Crosshair, Zap,
  TrendingUp, Users, MapPin, ArrowUpRight, ArrowDownRight,
  Cigarette, Dumbbell, Wine, UtensilsCrossed, Moon, AlertCircle, DollarSign, ExternalLink, Info
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { InsightBox } from '../components/InsightBox';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { ShareButtons } from '../components/ShareButtons';
import { SITE_MEDIA, CHRONIC_GALLERY } from '../lib/siteMedia';
import { STATE_FIPS, GEO_URL, getRiskColor } from '../lib/mapConstants';
import { fetchWithFallback } from '../lib/dataFetcher';
const API = process.env.REACT_APP_BACKEND_URL;

const DISEASE_ICONS = {
  diabetes: Activity, heart_disease: Heart, obesity: Scale, copd: Wind,
  arthritis: Bone, hypertension: Thermometer, cancer: Crosshair, stroke: Zap,
};
const RISK_ICONS = {
  smoking: Cigarette, physical_inactivity: Dumbbell, heavy_drinking: Wine,
  poor_diet: UtensilsCrossed, sleep_deprivation: Moon, chronic_stress: AlertCircle,
};

const riskBg = (level) => level === 'high' ? 'bg-red-50 border-red-200 text-red-700' : level === 'moderate' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border border-slate-200 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (<p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}%</p>))}
    </div>
  );
};

export default function ChronicDiseaseDashboard() {
  const [diseases, setDiseases] = useState({});
  const [selectedDisease, setSelectedDisease] = useState('diabetes');
  const [mapData, setMapData] = useState([]);
  const [trends, setTrends] = useState([]);
  const [demographics, setDemographics] = useState(null);
  const [riskFactors, setRiskFactors] = useState({});
  const [hoveredState, setHoveredState] = useState(null);

  useEffect(() => {
    fetchWithFallback('/api/chronic/diseases').then(d => d && setDiseases(d.diseases || {})).catch(() => {});
    fetchWithFallback('/api/chronic/risk-factors').then(d => d && setRiskFactors(d.factors || {})).catch(() => {});
  }, []);

  useEffect(() => {
    fetchWithFallback(`/api/chronic/map-data?disease=${selectedDisease}`).then(d => d && setMapData(d.data || [])).catch(() => {});
    fetchWithFallback(`/api/chronic/trends?disease=${selectedDisease}`).then(d => d && setTrends(d.trends || [])).catch(() => {});
    fetchWithFallback(`/api/chronic/demographics?disease=${selectedDisease}`).then(d => d && setDemographics(d)).catch(() => {});
  }, [selectedDisease]);

  const mapLookup = useMemo(() => { const m = {}; mapData.forEach(d => { m[d.state] = d; }); return m; }, [mapData]);
  const currentDisease = diseases[selectedDisease] || {};
  const diseaseSummary = selectedDisease === 'arthritis'
    ? 'Arthritis includes chronic joint conditions such as osteoarthritis that affect pain, mobility, and daily function.'
    : currentDisease.description || '';

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="chronic-disease-page">
      <PageSEO title="Chronic Disease Dashboard" description="Explore 14 chronic disease prevalence rates across all 50 US states. Interactive maps, risk factors, and state comparisons sourced from CDC BRFSS and NHIS data." path="/chronic-disease" />
      {/* Header with image */}
      <section className="relative bg-white border-b border-slate-200/60 overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-15">
          <img src={SITE_MEDIA.chronic} alt="" className="w-full h-full object-contain object-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-white" /></div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Chronic Disease Dashboard</h1>
            </div>
            <p className="text-base text-slate-500 max-w-3xl">Explore chronic disease prevalence, trends, demographics, and modifiable risk factors using clearly labeled CDC datasets. This page now focuses only on chronic disease patterns.</p>
            <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
              <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1">CDC PLACES 2025</span>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1">CDC BRFSS 2024</span>
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1">CDC NHIS 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* ========== DATA LINEAGE BANNER ========== */}
        <details className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 sm:p-5 group" data-testid="chronic-data-lineage">
          <summary className="cursor-pointer flex items-start gap-3 list-none">
            <Info className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-900">Data lineage — where every number on this page comes from</p>
              <p className="text-xs text-emerald-800 mt-0.5">Click to expand · we're transparent about what's live, what's bundled, and what's not measured.</p>
            </div>
          </summary>
          <div className="mt-4 space-y-3 text-xs text-emerald-900 leading-relaxed">
            <p><strong>✅ State-level prevalence (map):</strong> LIVE from CDC PLACES (<code className="text-[10px] bg-white/60 px-1.5 py-0.5 rounded">data.cdc.gov/resource/swc5-untb</code>), aggregated county → state via population-weighted average <code className="text-[10px]">SUM(rate × pop18plus) / SUM(pop18plus)</code>. Falls back to clearly-labeled estimates only if the CDC API is unreachable (the CSV's <code>Data Status</code> column shows official vs. estimated per row).</p>
            <p><strong>✅ National prevalence + annual deaths + cost burden:</strong> Sourced from the CDC topic-program pages linked under each disease card (CDC National Diabetes Statistics Report 2024, CDC NCHS, CDC NHIS, etc.). See per-disease <code>source_url</code> in the JSON API.</p>
            <p><strong>✅ Trend-over-time chart:</strong> National prevalence by year, sourced from CDC BRFSS / CDC National Diabetes Statistics Report / NHANES / NCHS, covering 6 diseases with reliable longitudinal series (diabetes, obesity, heart disease, COPD, hypertension, cancer mortality). For diseases without verified national trend series, the chart is <em>hidden</em> rather than fabricated.</p>
            <p><strong>⚠️ Demographic breakdowns (age / gender / race):</strong> The figures shown are from the <em>CDC National Diabetes Statistics Report 2024</em> as a reference for the shape of U.S. chronic disease disparities. We do <strong>not</strong> have authoritative per-disease demographic breakdowns bundled for every condition — when you switch diseases, the demographic chart is labeled accordingly. For per-disease breakdowns, follow the source link.</p>
            <p><strong>⚠️ Temporal map (year-by-year):</strong> The current-year layer is live CDC PLACES. Earlier years are <em>modeled estimates</em> back-projected from CDC national BRFSS trends — labeled <code>data_status = modeled</code> in the API.</p>
            <p><strong>🔗 Download the CSV</strong> below for the full methodology header (per-column formulas, exact CDC dataset IDs, and per-row data status).</p>
          </div>
        </details>

        {/* Chronic Disease Image Gallery - FULL IMAGES */}
        <div className="grid grid-cols-3 gap-4">
          {CHRONIC_GALLERY.map((img, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="rounded-xl overflow-hidden group relative bg-slate-50">
              <img src={img.src} alt={img.alt} className="w-full h-48 object-contain bg-slate-50 p-2" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-sm text-white font-medium">{img.caption}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="rounded-2xl border border-violet-200/60 bg-white/90 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-violet-700 mb-2">How to read this page</p>
          <p className="text-sm text-slate-600 leading-relaxed">Each chart answers three questions: what you’re looking at, what the pattern means, and why it matters for prevention, screening, or care. Hover on maps and charts for exact values.</p>
        </div>

            {/* Disease Selector */}
            <div className="flex flex-wrap gap-2" data-testid="disease-selector">
              {Object.entries(diseases).map(([key, d]) => {
                const Icon = DISEASE_ICONS[key] || Activity;
                return (
                  <button key={key} onClick={() => setSelectedDisease(key)} data-testid={`disease-btn-${key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${key === selectedDisease ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                    <Icon className="w-4 h-4" /> {d.name}
                  </button>
                );
              })}
            </div>

            {/* Disease Overview Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div key={selectedDisease + '-prev'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">US Prevalence</p>
                <p className="text-3xl font-bold" style={{ color: currentDisease.color }}>{currentDisease.us_prevalence}%</p>
                <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{diseaseSummary}</p>
              </motion.div>
              <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">Annual Deaths</p>
                <p className="text-3xl font-bold text-slate-900">{currentDisease.annual_deaths?.toLocaleString() || 'N/A'}</p>
                <p className="text-xs text-slate-400 mt-1.5">{currentDisease.trend === 'increasing' ? 'Trend: Increasing' : currentDisease.trend === 'decreasing' ? 'Trend: Decreasing' : 'Trend: Stable'}</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Cost Burden</p>
                <p className="text-3xl font-bold text-slate-900">{currentDisease.cost_burden || 'N/A'}</p>
                <p className="text-xs text-slate-400 mt-1.5">Annual US healthcare cost</p>
              </div>
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
                <p className="text-xs text-amber-700 font-semibold mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Key Fact</p>
                <p className="text-sm text-slate-800 font-medium leading-snug">{currentDisease.key_fact || ''}</p>
              </div>
            </div>

            {/* US Map */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="chronic-disease-map">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-teal-600" /> State-Level {currentDisease.name || ''} Rates</h2>
                <div className="flex items-center gap-3">
                  <CsvDownloadButton dataset="chronic" disease={selectedDisease} />
                  <ShareButtons title={`${currentDisease.name} State-Level Rates`} />
                </div>
              </div>
              {/* Legend - positioned outside map */}
              <div className="flex items-center justify-end gap-4 text-xs mb-2">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Low</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500" /> Moderate</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> High</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-200" /> No Data</span>
              </div>
              <div className="w-full overflow-hidden rounded-xl bg-slate-50 border border-slate-200">
                <ComposableMap 
                  projection="geoAlbersUsa" 
                  projectionConfig={{ scale: 1000 }}
                  width={900} 
                  height={500}
                  style={{ width: '100%', height: 'auto' }}
                  data-testid="us-map"
                >
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) => geographies.map((geo) => {
                      const stateAbbr = STATE_FIPS[geo.id];
                      const sd = mapLookup[stateAbbr];
                      return (
                        <Geography key={geo.rsmKey || geo.id} geography={geo}
                          fill={hoveredState === stateAbbr ? '#1e293b' : sd ? getRiskColor(sd.risk_level) : '#e2e8f0'}
                          stroke="#fff" strokeWidth={0.75}
                          onMouseEnter={() => setHoveredState(stateAbbr)}
                          onMouseLeave={() => setHoveredState(null)}
                          style={{ default: { outline: 'none' }, hover: { outline: 'none', cursor: 'pointer' }, pressed: { outline: 'none' } }} />
                      );
                    })}
                  </Geographies>
                </ComposableMap>
              </div>
              {hoveredState && mapLookup[hoveredState] && (
                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-3" data-testid="map-tooltip">
                  <span className="font-bold text-slate-900 text-lg">{hoveredState}</span>
                  <span>Rate: <strong>{mapLookup[hoveredState].rate}%</strong></span>
                  <span>US Avg: {mapLookup[hoveredState].us_average}%</span>
                  <span>Diff: {mapLookup[hoveredState].difference > 0 ? '+' : ''}{mapLookup[hoveredState].difference}%</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${riskBg(mapLookup[hoveredState].risk_level)}`}>{mapLookup[hoveredState].risk_level}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${mapLookup[hoveredState].data_status === 'estimated' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {mapLookup[hoveredState].data_status === 'estimated' ? 'Estimate' : 'Official'}
                  </span>
                  {mapLookup[hoveredState].coverage_note && <span className="basis-full text-xs text-slate-500">{mapLookup[hoveredState].coverage_note}</span>}
                </div>
              )}
              {hoveredState && !mapLookup[hoveredState] && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="map-no-data">
                  No recent CDC data is available for {hoveredState} for this metric right now.
                </div>
              )}
            </div>

            <InsightBox seeing={`State-level ${currentDisease.name || ''} rates across the US, color-coded by risk level relative to the national average.`} matters="Geographic disparities reveal where chronic disease burden is highest. Southern states consistently show higher rates due to poverty, limited healthcare access, and food deserts." doAbout="Know your state's profile. If you live in a high-risk area, prioritize preventive care, screenings, and healthy lifestyle choices." source="CDC Chronic Disease Indicators 2024" />

            {/* Trends + Demographics */}
            {(() => {
              const hasTrends = trends.length > 0;
              const hasDemo = demographics && (demographics.by_age?.length || demographics.by_gender?.length || demographics.by_race?.length);
              const gridCols = (hasTrends && hasDemo) ? 'lg:grid-cols-2' : 'lg:grid-cols-1';
              return (
                <div className={`grid ${gridCols} gap-6`}>
                  {hasTrends ? (
                    <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="trend-chart">
                      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-teal-600" /> {currentDisease.name || ''} Trend Over Time</h2>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={trends}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                          <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" domain={['auto', 'auto']} unit="%" />
                          <Tooltip content={<ChartTooltip />} />
                          <Line type="monotone" dataKey="rate" stroke={currentDisease.color || '#0d9488'} strokeWidth={3} dot={{ r: 5, fill: currentDisease.color || '#0d9488' }} name="Rate" />
                        </LineChart>
                      </ResponsiveContainer>
                      <InsightBox
                        seeing={`How ${currentDisease.name || ''} rates have changed over time nationally.`}
                        matters={currentDisease.trend === 'increasing' ? 'This disease is getting MORE common — meaning more people are at risk each year.' : 'While stable, this disease still affects millions and remains a leading cause of disability or death.'}
                        doAbout="Trends inform prevention strategies. If rates are rising, talk to your doctor about early screening and risk reduction."
                        source="CDC BRFSS national prevalence (CDC Diabetes Statistics Report, NHANES, NCHS) 2008–2024"
                      />
                    </div>
                  ) : null}
                  {hasDemo ? (
                    <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="demographics-chart">
                      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Demographics</h2>
                      <div className="space-y-5">
                        {demographics.by_age?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">By Age Group</p>
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart data={demographics.by_age} layout="vertical">
                                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" unit="%" />
                                <YAxis type="category" dataKey="group" tick={{ fontSize: 11 }} stroke="#94a3b8" width={45} />
                                <Tooltip content={<ChartTooltip />} />
                                <Bar dataKey="rate" name="Rate" radius={[0, 6, 6, 0]}>
                                  {demographics.by_age.map((_, idx) => <Cell key={idx} fill={['#10b981','#22c55e','#84cc16','#f59e0b','#f97316','#ef4444'][idx] || '#0d9488'} />)}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                        <div className="grid gap-5 lg:grid-cols-2">
                          {demographics.by_gender?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">By Gender</p>
                              <ResponsiveContainer width="100%" height={140}>
                                <BarChart data={demographics.by_gender}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                  <XAxis dataKey="group" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" unit="%" />
                                  <Tooltip content={<ChartTooltip />} />
                                  <Bar dataKey="rate" name="Rate" radius={[6, 6, 0, 0]} fill="#8b5cf6" />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {demographics.by_race?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">By Race/Ethnicity</p>
                              <ResponsiveContainer width="100%" height={160}>
                                <BarChart data={demographics.by_race} layout="vertical">
                                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" unit="%" />
                                  <YAxis type="category" dataKey="group" tick={{ fontSize: 11 }} stroke="#94a3b8" width={80} />
                                  <Tooltip content={<ChartTooltip />} />
                                  <Bar dataKey="rate" name="Rate" fill="#0d9488" radius={[0, 6, 6, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                        </div>
                        <InsightBox
                          seeing={`How ${currentDisease.name || ''} differs across age, gender, and race/ethnicity groups.`}
                          matters="Differences between groups can reflect exposure patterns, structural barriers to care, screening access, comorbidities, and social determinants of health."
                          doAbout="Understanding who is most affected helps target earlier screening, more equitable prevention efforts, and better resource allocation."
                          source={demographics.source || 'CDC'}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* Risk Factors */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="risk-factors">
              <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2"><Activity className="w-5 h-5 text-teal-600" /> Key Risk Factors</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(riskFactors).map(([key, f]) => {
                  const Icon = RISK_ICONS[key] || Activity;
                  const impactColor = f.impact === 'high' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';
                  return (
                    <div key={key} className="rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-shadow" data-testid={`risk-factor-${key}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-600" /></div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{f.label}</p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${impactColor}`}>{f.impact} impact</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-2">{f.detail}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1"><span>US Rate</span><span className="font-semibold text-slate-700">{f.us_rate}%</span></div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-700" style={{ width: `${Math.min(f.us_rate, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <InsightBox
                seeing="The national prevalence of major modifiable chronic disease risk factors such as smoking, poor diet, inactivity, and insufficient sleep."
                matters="These factors do not just accompany chronic disease burden — they help drive it. High percentages suggest where prevention efforts can produce the biggest gains."
                doAbout="Risk factor data points to practical interventions: tobacco cessation, blood pressure control, healthier food access, physical activity, and sleep support."
                source="CDC BRFSS 2024"
              />
            </div>

            {/* Top/Bottom States */}
            <div className="grid lg:grid-cols-2 gap-6">
              {[
                { title: 'Highest Rates', data: [...mapData].sort((a, b) => b.rate - a.rate).slice(0, 10), icon: ArrowUpRight, iconColor: 'text-red-500' },
                { title: 'Lowest Rates', data: [...mapData].sort((a, b) => a.rate - b.rate).slice(0, 10), icon: ArrowDownRight, iconColor: 'text-emerald-500' },
              ].map(({ title, data, icon: Icon, iconColor }) => (
                <div key={title} className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Icon className={`w-5 h-5 ${iconColor}`} /> {title}</h2>
                  <div className="space-y-1.5">
                    {data.map((s, i) => (
                      <div key={s.state} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                        <span className="text-xs font-mono text-slate-400 w-5">{i + 1}</span>
                        <span className="text-sm font-bold text-slate-900 w-8">{s.state}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.rate / 50) * 100}%`, backgroundColor: getRiskColor(s.risk_level) }} /></div>
                        <span className="text-sm font-semibold text-slate-700 w-14 text-right">{s.rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <InsightBox
              seeing={`The ten states with the highest and lowest reported ${currentDisease.name || ''} prevalence compared with the rest of the country.`}
              matters="Rankings help reveal where chronic disease burden is concentrated and where prevention, access, and policy interventions may be especially important."
              doAbout="Use rankings as a starting point, not a final judgment — state context matters, including age structure, poverty, healthcare access, and local prevention programs."
              source="CDC Chronic Disease Indicators 2024"
            />

        <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="chronic-interpretation-note">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Why these chronic disease differences matter</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700 mb-2">Geography</p>
              <p className="text-sm text-slate-600 leading-relaxed">State variation often reflects a mix of income, food access, insurance coverage, built environment, and screening availability.</p>
            </div>
            <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700 mb-2">Time trends</p>
              <p className="text-sm text-slate-600 leading-relaxed">Rising lines suggest growing burden, while flatter lines can still indicate major ongoing impact if millions are affected year after year.</p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700 mb-2">Action</p>
              <p className="text-sm text-slate-600 leading-relaxed">These data are most useful when tied to prevention: blood pressure control, diabetes screening, tobacco cessation, healthy weight, and routine primary care.</p>
            </div>
          </div>
        </section>

        {/* Data Sources & Download */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="chronic-data-sources">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-red-600" /> Data Sources & Download
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Where this data comes from:</p>
              <ul className="space-y-2">
                {[
                  { name: "CDC PLACES (Population Level Analysis)", url: "https://www.cdc.gov/places" },
                  { name: "CDC BRFSS (Behavioral Risk Factor Surveillance)", url: "https://www.cdc.gov/brfss/index.html" },
                  { name: "CDC NHIS (National Health Interview Survey)", url: "https://www.cdc.gov/nchs/nhis/index.html" },
                  { name: "CDC NCHS (National Center for Health Statistics)", url: "https://www.cdc.gov/nchs" },
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-red-700 transition-colors">
                      {s.name} <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Download & Resources:</p>
              <p className="text-xs text-slate-500 mb-3">Download state-level data as CSV for your own analysis.</p>
              <CsvDownloadButton dataset="chronic" disease={selectedDisease} label="Download Chronic Disease Data (CSV)" />
              {currentDisease.resources?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Support Organizations for {currentDisease.name}:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentDisease.resources.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors font-medium"
                        data-testid={`chronic-resource-${selectedDisease}-${i}`}
                      >
                        {r.name} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center py-4">Sources: CDC Chronic Disease Indicators 2024 · CDC BRFSS 2024 · CDC NHIS 2024</p>
      </div>
    </div>
  );
}
